import React, { useState, useRef } from 'react';
import '../css/editMediaOverlay.css';
import CloseButton from '../../../button-fs-cp/close-button-op/ts/closeButton';
import { Button, Empty } from '@core/uilibrary';
import RecentReplaceMedia from '../../../dashboard-fs-cp/recent-activity-dsb-op/recent-replace-media/ts/recentReplaceMedia';
import TextField from '../../../input-fs-cp/text-field-op/ts/textField';
import TextArea from '../../../input-fs-cp/textarea-field-op/ts/textArea';
import { UploadIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

/* ── Types ───────────────────────────────────────────────── */

export interface RecentMediaItem {
  id: string;
  imageSrc: string;
  fileName: string;
  fileSize: string;
}

export interface EditMediaFormData {
  fileName: string;
  altText?: string;
  imageSrc: string;
  files?: File[];
  description?: string; // ✅ added — passed to edit-file API
}
export interface EditMediaOverlayProps {
  imageSrc: string;
  fileName: string;
  altText: string;
  description: string;
  onClose?: () => void;
  onSave?: (data: EditMediaFormData) => void;
}

/* ── Helpers ─────────────────────────────────────────────── */

// Format bytes into a readable string e.g. "1.2MB"
const formatBytes = (bytes: number): string => {
  /* istanbul ignore next */
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))}${sizes[i]}`;
};

/* ── Component ───────────────────────────────────────────── */

const EditMediaOverlay: React.FC<EditMediaOverlayProps> = ({
  imageSrc: initialImageSrc,
  fileName: initialFileName,
  altText: initialAltText,
  description: initialDescription,
  onClose,
  onSave,
}) => {
  const [imageSrc, setImageSrc] = useState(initialImageSrc);
  const [fileName, setFileName] = useState(initialFileName);
  const [altText, setAltText] = useState(initialAltText);
  const [description, setDescription] = useState(initialDescription);
  const [files, setFiles] = useState<File[]>([]);

  // ── Recent list is internal only — starts empty every time the overlay opens.
  // Items are added only when the user replaces the image from their device.
  const [recentList, setRecentList] = useState<RecentMediaItem[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const replaceCount = useRef(0);

  /* ── Replace image ── */
  const handleReplaceClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const prevImageSrc = imageSrc;
    const prevFileName = fileName;

    replaceCount.current += 1;
    const recentId = `recent-${Date.now()}-${replaceCount.current}`;

    setRecentList((prev) => [
      {
        id: recentId,
        imageSrc: prevImageSrc,
        fileName: prevFileName,
        fileSize: formatBytes(file.size),
      },
      ...prev,
    ]);

    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setFileName(file.name);
    setFiles([file]); // ← store the actual File object
    e.target.value = '';
  };

  /* ── Delete a recent item ── */
  const handleDeleteRecent = (id: string) => {
    setRecentList((prev) => prev.filter((item) => item.id !== id));
  };

  /* ── Select a recent item as the current image ── */
  const handleSelectRecent = (item: RecentMediaItem) => {
    setImageSrc(item.imageSrc);
    setFileName(item.fileName);
  };

  /* ── Save ── */
  const handleSave = () => {
    onSave?.({ fileName, altText, files, imageSrc, description });
  };

  return (
    <div className="scl--cms-components-editMedia-modal">

      {/* ── Header ── */}
      <div className="scl--cms-components-editMedia-header">
        <h2 className="scl--cms-components-editMedia-title">Edit file</h2>
        <div className="scl--cms-components-editMedia-closeBtn">
          <CloseButton onClick={onClose} aria-label="Close edit file" />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="scl--cms-components-editMedia-body">

        {/* ── Left Panel ── */}
        <div className="scl--cms-components-editMedia-left">

          {/* Image preview */}
          <div className="scl--cms-components-editMedia-imageWrapper">
            <img
              src={imageSrc}
              alt={altText || fileName}
              className="scl--cms-components-editMedia-image"
            />
          </div>

          <div className="scl--cms-components-editMedia-replaceBtn">
            <Button width={100} value="Replace Media" variant="ghost" icon={<UploadIcon />} onClick={handleReplaceClick} />
          </div>


          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="scl--cms-components-editMedia-fileInput"
            onChange={handleFileChange}
          />

          {/* Divider */}
          <div className="scl--cms-components-editMedia-recentDivider" />

          {/* Recent images section */}
          <div className="scl--cms-components-editMedia-recentSection">

            {/* Label */}
            <span className="scl--cms-components-editMedia-recentLabel">
              {recentList.length > 0
                ? `Recent media(${recentList.length})`
                : 'Recent media'}
            </span>

            {/* Empty state — shown when no image has been replaced yet */}
            {recentList.length === 0 ? (

              <div className="scl--cms-components-editMedia-emptyWrapper">
                <Empty
                  title="No recent Media"
                />
              </div>

            ) : (

              /* Scrollable list */
              <div className="scl--cms-components-editMedia-recentList">
                {recentList.map((item) => (
                  <div //NOSONAR
                    key={item.id}
                    className="scl--cms-components-editMedia-recentItem"
                    onClick={() => handleSelectRecent(item)}
                  >
                    <RecentReplaceMedia
                      imageSrc={item.imageSrc}
                      fileName={item.fileName}
                      fileSize={item.fileSize}
                      onDelete={(e: React.MouseEvent | undefined) => {
                        e?.stopPropagation();
                        handleDeleteRecent(item.id);
                      }}
                    />
                  </div>
                ))}
              </div>

            )}

          </div>
        </div>

        {/* ── Vertical Divider ── */}
        <div className="scl--cms-components-editMedia-divider" />

        {/* ── Right Panel ── */}
        <div className="scl--cms-components-editMedia-right">

          {/* Form fields */}
          <div className="scl--cms-components-editMedia-fields">

            {/* File name */}
            <TextField
              name="fileName"
              label="File name"
              value={fileName}
              onChange={setFileName}
            />

            {/* Alt text */}
            <TextField
              name="altText"
              label="Alt text"
              value={altText}
              onChange={setAltText}
            />

            {/* Description */}
            <TextArea
              name="description"
              label="Description"
              value={description}
              onChange={setDescription}
              rows={3}
              maxLength={150}
            />

          </div>

        </div>
      </div>

      {/* ── Footer ── */}
      <div className="scl--cms-components-editMedia-footer">
        <Button
          variant="ghost"
          size="md"
          value="Cancel"
          type="button"
          onClick={onClose}
        />
        <Button
          variant="primary"
          size="md"
          value="Save"
          type="button"
          onClick={handleSave}
        />
      </div>

    </div>
  );
};

export default EditMediaOverlay;